import { NgOptimizedImage, isPlatformBrowser } from '@angular/common'
import type { AfterViewInit, ElementRef, OnDestroy } from '@angular/core'
import {
  Component,
  HostListener,
  Input,
  PLATFORM_ID,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core'
import { RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'

import type { EditionDto, MagazineDto } from '../../../../../api/responses'
import { ArticleForNamePipe } from '../../../../../shared/pipes/article-for-name.pipe'
import { LocalizedDatePipe } from '../../../../../shared/pipes/localized-date.pipe'

@Component({
  selector: 'dhp-previous-editions-section',
  templateUrl: './previous-editions-section.component.html',
  styleUrls: ['./previous-editions-section.component.scss'],
  imports: [
    RouterLink,
    NgOptimizedImage,
    TranslatePipe,
    ArticleForNamePipe,
    LocalizedDatePipe,
  ],
})
export class PreviousEditionsSectionComponent
  implements AfterViewInit, OnDestroy
{
  private platformId = inject(PLATFORM_ID)

  readonly scrollContainer = viewChild.required<ElementRef>('scrollContainer')

  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input({ required: true }) magazine!: MagazineDto
  readonly currentEdition = input<EditionDto>()
  /** All of the magazine's editions, as the edition page already has them. */
  readonly editions = input.required<EditionDto[]>()
  readonly previousEditions = computed(() =>
    this.editions()
      .filter(edition => isBefore(edition, this.currentEdition()))
      .sort((a, b) => (isBefore(a, b) ? 1 : -1))
  )
  readonly currentPage = signal(1)
  visibleEditions = 0

  isDragging = false
  startX = 0
  scrollLeftValue = 0

  // Declare scrollListener as optional without initializing it
  private scrollListener?: () => void

  get pages(): number[] {
    if (!this.visibleEditions || this.visibleEditions <= 0) {
      return [1]
    }
    const pages = Math.ceil(this.editions().length / this.visibleEditions)
    return Array.from({ length: pages }, (_, i) => i + 1)
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.calculateVisibleEditions()
        this.setupScrollListener()
        this.gotoPage(this.currentPage(), false) // Initialize scroll position without animation
      })
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.removeScrollListener()
    }
  }

  onDragStart(event: MouseEvent | TouchEvent): void {
    this.isDragging = true
    if (event instanceof MouseEvent) {
      this.startX =
        event.pageX - this.scrollContainer().nativeElement.offsetLeft
    } else {
      this.startX =
        event.touches[0].pageX - this.scrollContainer().nativeElement.offsetLeft
    }
    this.scrollLeftValue = this.scrollContainer().nativeElement.scrollLeft
  }

  onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return
    event.preventDefault()
    let x: number
    if (event instanceof MouseEvent) {
      x = event.pageX - this.scrollContainer().nativeElement.offsetLeft
    } else {
      x =
        event.touches[0].pageX - this.scrollContainer().nativeElement.offsetLeft
    }
    const walk = (x - this.startX) * 2 // Scroll-fast
    this.scrollContainer().nativeElement.scrollLeft =
      this.scrollLeftValue - walk
  }

  onDragEnd(): void {
    this.isDragging = false
  }

  scrollPreviousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1)
      this.gotoPage(this.currentPage())
    }
  }

  scrollNextPage(): void {
    const maxPages = this.pages.length
    if (this.currentPage() < maxPages) {
      this.currentPage.update(v => v + 1)
      this.gotoPage(this.currentPage())
    }
  }

  gotoPage(page: number, animate = true): void {
    if (page < 1 || page > this.pages.length) return

    const scrollContainerValue = this.scrollContainer()
    if (scrollContainerValue?.nativeElement) {
      const scrollContainer = scrollContainerValue.nativeElement
      const editionElement = scrollContainer.querySelector('.edition-card')
      if (!editionElement) return

      const editionWidth = (editionElement as HTMLElement).offsetWidth + 16
      const totalPages = this.pages.length

      let scrollPosition: number

      if (page === totalPages) {
        // For the last page, scroll to the very end
        scrollPosition =
          scrollContainer.scrollWidth - scrollContainer.clientWidth
      } else {
        scrollPosition = (page - 1) * (editionWidth * this.visibleEditions)
      }

      scrollContainerValue.nativeElement.scrollTo({
        left: scrollPosition,
        behavior: animate ? 'smooth' : 'auto',
      })
    }
  }

  updateCurrentPage(): void {
    const scrollContainer = this.scrollContainer()
    if (scrollContainer?.nativeElement) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainer.nativeElement
      const editionElement =
        scrollContainer.nativeElement.querySelector('.edition-card')
      if (!editionElement) return

      const editionWidth = (editionElement as HTMLElement).offsetWidth + 16
      const editionsPerPage = this.visibleEditions

      // Calculate current page based on scroll position
      let currentScrollPage =
        Math.round(scrollLeft / (editionWidth * editionsPerPage)) + 1

      const totalPages = this.pages.length

      // If we're very close to the end, snap to last page
      if (scrollLeft + clientWidth >= scrollWidth - editionWidth / 2) {
        currentScrollPage = totalPages
      }

      // Ensure currentScrollPage is within bounds
      currentScrollPage = Math.min(Math.max(currentScrollPage, 1), totalPages)

      if (currentScrollPage !== this.currentPage()) {
        this.currentPage.set(currentScrollPage)
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.calculateVisibleEditions()
      this.gotoPage(this.currentPage(), false)
    }
  }

  private setupScrollListener(): void {
    const scrollContainer = this.scrollContainer()
    if (scrollContainer?.nativeElement) {
      // Bind the updateCurrentPage method to 'this' context
      this.scrollListener = this.updateCurrentPage.bind(this)
      scrollContainer.nativeElement.addEventListener(
        'scroll',
        this.scrollListener
      )
    }
  }

  private removeScrollListener(): void {
    const scrollContainer = this.scrollContainer()
    if (scrollContainer?.nativeElement && this.scrollListener) {
      scrollContainer.nativeElement.removeEventListener(
        'scroll',
        this.scrollListener
      )
      this.scrollListener = undefined // Clean up the listener reference
    }
  }

  private calculateVisibleEditions(): void {
    const scrollContainer = this.scrollContainer()
    if (scrollContainer?.nativeElement) {
      const containerWidth = scrollContainer.nativeElement.clientWidth
      // Query for the first edition card to determine its width
      const editionElement =
        scrollContainer.nativeElement.querySelector('.edition-card')
      if (editionElement) {
        const editionWidth = (editionElement as HTMLElement).offsetWidth + 16
        this.visibleEditions = Math.floor(containerWidth / editionWidth) || 1
      }
    }
  }
}

function isBefore(edition: EditionDto, current?: EditionDto): boolean {
  return (edition?.createdAt ?? -1) < (current?.createdAt ?? -1)
}
